import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLongRightIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import CreditBuyModal from './components/CreditBuyModal';
import CompanySettingsModal from './components/CompanySettingsModal';
import { AuthContext } from '../AuthContext';

const API_BASE = 'https://api.insightos.co.uk';
const DURATION_CREDIT_COST = { 4: 10, 8: 20, 12: 30 };
const AD_STUDIO_DRAFT_KEY = 'avenor_ad_studio_draft_v1';
const MAX_BRAND_REFERENCE_BYTES = 10 * 1024 * 1024;
const BRAND_REFERENCE_ALLOWED_MIME_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
const BRAND_REFERENCE_ALLOWED_SIZES = new Set(['1280x720', '720x1280', '1792x1024', '1024x1792']);

const STYLE_OPTIONS = [
	'Cinematic',
	'Corporate',
	'Cartoon',
	'Minimal',
	'Black & White',
	'Retro',
	'Modern Startup',
];

const TONE_OPTIONS = [
	'Professional',
	'Friendly',
	'Dramatic',
	'Funny',
	'Inspirational',
];

const CAMERA_OPTIONS = [
	'Wide Shot',
	'Close Up',
	'Over Shoulder',
	'Tracking Shot',
	'Drone Shot',
	'Static',
	'Cinematic Pan',
	'Zoom In',
];

const CHARACTER_TYPE_OPTIONS = [
	'Business Owner',
	'Salesperson',
	'Customer',
	'Team',
	'CEO',
	'Generic Person',
];

const CHARACTER_STYLE_OPTIONS = ['Realistic', 'Cartoon', '3D', 'Silhouette'];
const CHARACTER_AGE_OPTIONS = ['Young Adult', 'Middle Age', 'Senior'];

const ENVIRONMENT_OPTIONS = [
	'Office',
	'Warehouse',
	'Factory',
	'Construction Site',
	'Retail Shop',
	'Restaurant',
	'Tech Office',
	'Outdoors',
];

const VISUAL_STYLE_OPTIONS = [
	'Cinematic',
	'Documentary',
	'Corporate',
	'Cartoon',
	'Anime',
	'Sketch',
	'Black & White',
	'Vintage Film',
	'Hyper Realistic',
];

const LIGHTING_OPTIONS = ['Natural', 'Dramatic', 'Bright'];
const TIME_OF_DAY_OPTIONS = ['Morning', 'Afternoon', 'Night'];
const MOTION_SPEED_OPTIONS = ['Slow Motion', 'Normal', 'Fast'];

const createInitialScene = (label, defaults = {}) => ({
	label,
	description: defaults.description || '',
	camera: defaults.camera || 'Wide Shot',
	characters: defaults.characters || '',
	environment: defaults.environment || 'Office',
	action: defaults.action || '',
	mood: defaults.mood || '',
});

export default function Advert() {
	const navigate = useNavigate();
	const [campaignBrief, setCampaignBrief] = useState('');
	const [duration, setDuration] = useState('');
	const [style, setStyle] = useState('Cinematic');
	const [tone, setTone] = useState('Professional');
	const [visualStyle, setVisualStyle] = useState('Cinematic');
	const [characterType, setCharacterType] = useState('Business Owner');
	const [characterStyle, setCharacterStyle] = useState('Realistic');
	const [characterAge, setCharacterAge] = useState('Middle Age');
	const [lighting, setLighting] = useState('Natural');
	const [timeOfDay, setTimeOfDay] = useState('Morning');
	const [motionSpeed, setMotionSpeed] = useState('Normal');
	const [brandColour, setBrandColour] = useState('');
	const [productName, setProductName] = useState('InsightOS Avenor');
	const [brandReferenceType, setBrandReferenceType] = useState('logo');
	const [brandReferenceFile, setBrandReferenceFile] = useState(null);
	const [brandReferenceSize, setBrandReferenceSize] = useState('');
	const [callToAction, setCallToAction] = useState('Book a demo today');
	const [characterList, setCharacterList] = useState([
		{ type: 'Business Owner', style: 'Realistic', age: 'Middle Age', role: 'Primary decision maker' },
	]);
	const [scene1, setScene1] = useState(() =>
		createInitialScene('Scene 1 – Beginning', {
			environment: 'Office',
			action: 'Looking stressed at paperwork',
			camera: 'Close Up',
			mood: 'Frustrated',
		})
	);
	const [scene2, setScene2] = useState(() =>
		createInitialScene('Scene 2 – Middle', {
			environment: 'Office',
			action: 'Discovering InsightOS dashboard',
			camera: 'Over Shoulder',
			mood: 'Curious',
		})
	);
	const [scene3, setScene3] = useState(() =>
		createInitialScene('Scene 3 – End', {
			environment: 'Office',
			action: 'Business growing',
			camera: 'Wide Shot',
			mood: 'Success',
		})
	);
	const [storyboardAiLoading, setStoryboardAiLoading] = useState(false);

	const [jobId, setJobId] = useState('');
	const [status, setStatus] = useState('idle');
	const [progress, setProgress] = useState(0);
	const [videoUrl, setVideoUrl] = useState('');
	const [downloadUrl, setDownloadUrl] = useState('');
	const [creditsLoading, setCreditsLoading] = useState(false);
	const [salesCredits, setSalesCredits] = useState(0);
	const [creditsUsed, setCreditsUsed] = useState(0);
	const [buyModalOpen, setBuyModalOpen] = useState(false);
	const [settingsModalOpen, setSettingsModalOpen] = useState(false);
	const [companyForm, setCompanyForm] = useState({
		Email: '',
		Telephone: '',
		CompanyName: '',
		Industry: '',
		Website: '',
		ContactName: '',
		Address: '',
		Postcode: '',
		Description: '',
		EmailFooter: '',
	});
	const [savedEmailFooterHtml, setSavedEmailFooterHtml] = useState('');
	const [settingsSaving, setSettingsSaving] = useState(false);
	const [settingsFeedback, setSettingsFeedback] = useState('');
	const [settingsError, setSettingsError] = useState('');
	const [passwordOne, setPasswordOne] = useState('');
	const [passwordTwo, setPasswordTwo] = useState('');
	const [passwordSaving, setPasswordSaving] = useState(false);
	const [passwordFeedback, setPasswordFeedback] = useState('');
	const [passwordError, setPasswordError] = useState('');
	const [draftMessage, setDraftMessage] = useState('');
	const [lastSavedAt, setLastSavedAt] = useState('');

	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
 	const [gallerySaving, setGallerySaving] = useState(false);

	// Imagination section state
	const [imaginationPrompt, setImaginationPrompt] = useState('');
	const [imaginationLoading, setImaginationLoading] = useState(false);
	const [imaginationIdea, setImaginationIdea] = useState(null);
	const [imaginationDisplayText, setImaginationDisplayText] = useState('');
	const [imaginationLeads, setImaginationLeads] = useState([]);
	const [imaginationLeadsLoading, setImaginationLeadsLoading] = useState(false);

	const pollTimerRef = useRef(null);
	const savedGalleryJobsRef = useRef(new Set());
	const imaginationIntervalRef = useRef(null);
	const { user } = useContext(AuthContext);
	const permissionLevel = Number(user?.permission ?? user?.Permission ?? 0);
	const accessLevel = String(user?.access ?? user?.Access ?? '').trim().toLowerCase();
	const isFullAccessUser = accessLevel === 'admin' && permissionLevel === 1;
	const isPermissionRestricted = permissionLevel === 2 && !isFullAccessUser;
	const companyNameForMenu = String(user?.companyName ?? user?.CompanyName ?? '').trim();
	const showCompanyNameInMenu = Boolean(user?.insightAccount) && companyNameForMenu.length > 0;

	const navLinkClasses = ({ isActive }) => (
		`rounded-md px-3 py-2 text-sm font-medium transition ${isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`
	);

	const glassCardClass = 'rounded-2xl border border-white/55 bg-white/65 p-3 sm:p-4 shadow-[0_14px_40px_rgba(37,99,235,0.16)] backdrop-blur-xl';
	const glassInputClass = 'mt-1 block w-full rounded-md border border-white/70 bg-white/90 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200';
	const pageBackgroundStyle = {
		backgroundImage:
			"linear-gradient(135deg, rgba(219,234,254,0.9) 0%, rgba(233,213,255,0.82) 45%, rgba(255,255,255,0.92) 100%), url('/avenorlogobackground.png')",
	};

	const buildSceneLine = (scene) => {
		const values = [
			scene.description && `Description: ${scene.description}`,
			scene.environment && `Environment: ${scene.environment}`,
			scene.characters && `Characters: ${scene.characters}`,
			scene.action && `Action: ${scene.action}`,
			scene.camera && `Camera: ${scene.camera}`,
			scene.mood && `Mood: ${scene.mood}`,
		]
			.filter(Boolean)
			.join('. ');
		return `${scene.label}: ${values}`;
	};

	const finalPrompt = useMemo(() => {
		const castLine = characterList
			.map((character, index) => `${index + 1}. ${character.type}, ${character.style}, ${character.age}${character.role ? `, role: ${character.role}` : ''}`)
			.join(' | ');

		const lines = [
			`Create a ${style} advert video with a ${tone.toLowerCase()} tone.`,
			`Visual style: ${visualStyle}.`,
			`Character preset: ${characterType}, ${characterStyle}, ${characterAge}.`,
			characterList.length ? `Character cast: ${castLine}.` : null,
			`Lighting: ${lighting}. Time of day: ${timeOfDay}. Motion speed: ${motionSpeed}.`,
			campaignBrief ? `Campaign brief: ${campaignBrief}.` : null,
			buildSceneLine(scene1),
			buildSceneLine(scene2),
			buildSceneLine(scene3),
			productName ? `Product name: ${productName}.` : null,
			brandColour ? `Brand colour: ${brandColour}.` : null,
			brandReferenceFile
				? `Use the attached ${brandReferenceType === 'product' ? 'product reference image' : 'logo reference image'} as the opening visual reference, and preserve the same brand elements (logo, colors, and ${brandReferenceType === 'product' ? 'product appearance/screen details' : 'logo treatment'}) across every scene. Do not replace or redesign these reference elements.`
				: null,
			callToAction ? `Final call to action: ${callToAction}.` : null,
		]
			.filter(Boolean)
			.join('\n');

		return lines;
	}, [
		style,
		tone,
		visualStyle,
		characterType,
		characterStyle,
		characterAge,
		lighting,
		timeOfDay,
		motionSpeed,
		campaignBrief,
		scene1,
		scene2,
		scene3,
		productName,
		brandColour,
		brandReferenceType,
		brandReferenceFile,
		characterList,
		callToAction,
	]);

	const addCharacter = () => {
		setCharacterList((prev) => [
			...prev,
			{ type: 'Generic Person', style: 'Realistic', age: 'Young Adult', role: '' },
		]);
	};

	const removeCharacter = (indexToRemove) => {
		setCharacterList((prev) => prev.filter((_, index) => index !== indexToRemove));
	};

	const updateCharacter = (indexToUpdate, field, value) => {
		setCharacterList((prev) =>
			prev.map((character, index) =>
				index === indexToUpdate ? { ...character, [field]: value } : character
			)
		);
	};

	const extractCompanyRow = (payload) => {
		const rowsCandidate = payload?.data;
		if (Array.isArray(rowsCandidate)) {
			if (rowsCandidate.length > 0 && Array.isArray(rowsCandidate[0])) {
				return rowsCandidate[0][0] ?? null;
			}
			return rowsCandidate[0] ?? null;
		}
		return rowsCandidate && typeof rowsCandidate === 'object' ? rowsCandidate : null;
	};

	const fetchCompanyCredits = async () => {
		setCreditsLoading(true);
		try {
			const response = await fetch(`${API_BASE}/leads/company`, {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
			});

			if (!response.ok) {
				throw new Error('Failed to fetch company credits.');
			}

			const payload = await response.json();
			const row = extractCompanyRow(payload);
			const nextSalesCredits = Number(row?.SalesCredits ?? 0);
			const nextCreditsUsed = Number(row?.CreditsUsed ?? 0);

			setSalesCredits(Number.isFinite(nextSalesCredits) ? nextSalesCredits : 0);
			setCreditsUsed(Number.isFinite(nextCreditsUsed) ? nextCreditsUsed : 0);
			setCompanyForm((prev) => ({
				...prev,
				Email: String(row?.Email ?? ''),
				Telephone: String(row?.Telephone ?? ''),
				CompanyName: String(row?.CompanyName ?? ''),
				Industry: String(row?.Industry ?? ''),
				Website: String(row?.Website ?? ''),
				ContactName: String(row?.ContactName ?? ''),
				Description: String(row?.Description ?? ''),
				Address: String(row?.Address ?? ''),
				Postcode: String(row?.Postcode ?? ''),
				Description: String(row?.Description ?? ''),
				EmailFooter: String(row?.EmailFooter ?? ''),
			}));
			setSavedEmailFooterHtml(String(row?.EmailFooter ?? ''));
		} catch (err) {
			setError(err.message || 'Failed to load credits.');
		} finally {
			setCreditsLoading(false);
		}
	};

	useEffect(() => {
		fetchCompanyCredits();
	}, []);

	// Fetch leads for Imagination context
	useEffect(() => {
		const fetchImaginationLeads = async () => {
			setImaginationLeadsLoading(true);
			try {
				const response = await fetch(`${API_BASE}/leads/leadtable`, {
					method: 'GET',
					credentials: 'include',
				});
				const data = await response.json();
				setImaginationLeads(Array.isArray(data.leads) ? data.leads : []);
			} catch {
				setImaginationLeads([]);
			} finally {
				setImaginationLeadsLoading(false);
			}
		};
		fetchImaginationLeads();
	}, []);

	useEffect(() => {
		if (typeof window === 'undefined') return;

		try {
			const savedRaw = window.localStorage.getItem(AD_STUDIO_DRAFT_KEY);
			if (!savedRaw) return;

			const savedDraft = JSON.parse(savedRaw);
			if (!savedDraft || typeof savedDraft !== 'object') return;

			if (typeof savedDraft.campaignBrief === 'string') setCampaignBrief(savedDraft.campaignBrief);
			if (typeof savedDraft.duration === 'string') setDuration(savedDraft.duration);
			if (typeof savedDraft.style === 'string') setStyle(savedDraft.style);
			if (typeof savedDraft.tone === 'string') setTone(savedDraft.tone);
			if (typeof savedDraft.visualStyle === 'string') setVisualStyle(savedDraft.visualStyle);
			if (typeof savedDraft.characterType === 'string') setCharacterType(savedDraft.characterType);
			if (typeof savedDraft.characterStyle === 'string') setCharacterStyle(savedDraft.characterStyle);
			if (typeof savedDraft.characterAge === 'string') setCharacterAge(savedDraft.characterAge);
			if (typeof savedDraft.lighting === 'string') setLighting(savedDraft.lighting);
			if (typeof savedDraft.timeOfDay === 'string') setTimeOfDay(savedDraft.timeOfDay);
			if (typeof savedDraft.motionSpeed === 'string') setMotionSpeed(savedDraft.motionSpeed);
			if (typeof savedDraft.brandColour === 'string') setBrandColour(savedDraft.brandColour);
			if (typeof savedDraft.productName === 'string') setProductName(savedDraft.productName);
			if (savedDraft.brandReferenceType === 'product' || savedDraft.brandReferenceType === 'logo') {
				setBrandReferenceType(savedDraft.brandReferenceType);
			}
			if (typeof savedDraft.callToAction === 'string') setCallToAction(savedDraft.callToAction);
			if (Array.isArray(savedDraft.characterList) && savedDraft.characterList.length > 0) setCharacterList(savedDraft.characterList);

			if (savedDraft.scene1 && typeof savedDraft.scene1 === 'object') {
				setScene1((prev) => ({ ...prev, ...savedDraft.scene1 }));
			}
			if (savedDraft.scene2 && typeof savedDraft.scene2 === 'object') {
				setScene2((prev) => ({ ...prev, ...savedDraft.scene2 }));
			}
			if (savedDraft.scene3 && typeof savedDraft.scene3 === 'object') {
				setScene3((prev) => ({ ...prev, ...savedDraft.scene3 }));
			}

			if (typeof savedDraft.savedAt === 'string' && savedDraft.savedAt) {
				setLastSavedAt(savedDraft.savedAt);
			}

			setDraftMessage('Draft restored from local storage.');
		} catch {
			setDraftMessage('Could not restore saved draft.');
		}
	}, []);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const savedAt = new Date().toISOString();

		const payload = {
			campaignBrief,
			duration,
			style,
			tone,
			visualStyle,
			characterType,
			characterStyle,
			characterAge,
			lighting,
			timeOfDay,
			motionSpeed,
			brandColour,
			productName,
			brandReferenceType,
			callToAction,
			characterList,
			scene1,
			scene2,
			scene3,
			savedAt,
		};

		window.localStorage.setItem(AD_STUDIO_DRAFT_KEY, JSON.stringify(payload));
		setLastSavedAt(savedAt);
	}, [
		campaignBrief,
		duration,
		style,
		tone,
		visualStyle,
		characterType,
		characterStyle,
		characterAge,
		lighting,
		timeOfDay,
		motionSpeed,
		brandColour,
		productName,
		brandReferenceType,
		callToAction,
		characterList,
		scene1,
		scene2,
		scene3,
	]);

	const resetJob = () => {
		setJobId('');
		setStatus('idle');
		setProgress(0);
		setVideoUrl('');
		setDownloadUrl('');
		savedGalleryJobsRef.current = new Set();
	};

	const saveVideoToGallery = async ({ targetJobId, sourceUrl }) => {
		if (!targetJobId || !sourceUrl) return;
		if (savedGalleryJobsRef.current.has(targetJobId)) return;

		savedGalleryJobsRef.current.add(targetJobId);
		setGallerySaving(true);

		try {
			const fallbackTitle = `Advert ${new Date().toLocaleDateString()}`;
			const title = String(campaignBrief || productName || fallbackTitle).trim().slice(0, 120) || fallbackTitle;

			const response = await fetch(`${API_BASE}/gallery/add`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					title,
					url: sourceUrl,
					prompt: finalPrompt || null,
					credits: selectedDurationCost || null,
				}),
			});

			const payload = await response.json().catch(() => null);
			if (!response.ok || !payload?.success) {
				throw new Error(payload?.message || 'Failed to add video to gallery');
			}
		} catch (err) {
			console.error('Gallery save failed:', err);
			setError((prev) => prev || err?.message || 'Failed to save video to gallery.');
		} finally {
			setGallerySaving(false);
		}
	};

	const stopPolling = () => {
		if (pollTimerRef.current) {
			clearInterval(pollTimerRef.current);
			pollTimerRef.current = null;
		}
	};

	const pollStatus = async (targetJobId) => {
		if (!targetJobId) return;
		try {
			const response = await fetch(`${API_BASE}/leads/sora/prompt/${encodeURIComponent(targetJobId)}`, {
				method: 'GET',
				credentials: 'include',
			});
			const data = await response.json();
			if (!response.ok) {
				throw new Error(data?.message || 'Failed to poll video status');
			}

			setStatus(data.status || 'processing');
			setProgress(Number(data.progress ?? 0));

			if (data.status === 'completed') {
				const resolvedVideoUrl = data.videoUrl
					? (String(data.videoUrl).startsWith('http') ? data.videoUrl : `${API_BASE}${data.videoUrl}`)
					: '';
				const resolvedDownloadUrl = data.downloadUrl
					? (String(data.downloadUrl).startsWith('http') ? data.downloadUrl : `${API_BASE}${data.downloadUrl}`)
					: `${API_BASE}/leads/sora/prompt/${targetJobId}/download`;
				setVideoUrl(resolvedVideoUrl);
				setDownloadUrl(resolvedDownloadUrl);
				await saveVideoToGallery({ targetJobId, sourceUrl: resolvedDownloadUrl });
				stopPolling();
			}
		} catch (err) {
			setError(err?.message || 'Error polling video job.');
			stopPolling();
		}
	};

	useEffect(() => {
		if (!jobId) return;
		if (status === 'completed' || status === 'failed') return;

		stopPolling();
		pollTimerRef.current = setInterval(() => {
			pollStatus(jobId);
		}, 2000);

		// Poll immediately
		pollStatus(jobId);

		return () => stopPolling();
	}, [jobId, status]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		resetJob();

		if (isPermissionRestricted) {
			setError('Your permission level does not allow Ad Studio features.');
			return;
		}

		const trimmed = finalPrompt.trim();
		if (!trimmed) {
			setError('Please complete your storyboard before generating.');
			return;
		}

		const parsedDuration = Number(duration);
		const allowed = [4, 8, 12];
		if (!Number.isFinite(parsedDuration) || !allowed.includes(parsedDuration)) {
			setError('Please choose 4, 8, or 12 seconds.');
			return;
		}

		const requiredCredits = DURATION_CREDIT_COST[parsedDuration] || 0;
		if (salesCredits < requiredCredits) {
			setError(
				`Not enough credits. ${parsedDuration}s requires ${requiredCredits} credits, but you have ${salesCredits}.`
			);
			return;
		}

		setSubmitting(true);
		try {
			const queueTitle = String(campaignBrief || productName || 'Avenor advert').trim().slice(0, 120) || 'Avenor advert';
			const formData = new FormData();
			formData.append('prompt', trimmed);
			formData.append('seconds', String(parsedDuration));
			formData.append('title', queueTitle);
			formData.append('imageReferenceType', brandReferenceType);
			if (brandReferenceFile) {
				formData.append('brandReferenceImage', brandReferenceFile);
				if (brandReferenceSize) {
					formData.append('imageReferenceSize', brandReferenceSize);
				}
			}

			const response = await fetch(`${API_BASE}/leads/sora/prompt`, {
				method: 'POST',
				body: formData,
				credentials: 'include',
			});
			const data = await response.json();
			if (!response.ok) {
				throw new Error(data?.message || 'Failed to start video job');
			}

			if (data?.queued && data?.jobId) {
				navigate(`/avenorloading/advert/${data.jobId}`, {
					state: {
						title: 'Generating advert',
						backPath: '/gallery',
						returnLabel: 'Return to Gallery',
					},
				});
				return;
			}

			setJobId(data.jobId || '');
			setStatus(data.status || 'queued');
			setProgress(Number(data.progress ?? 0));
			if (Number.isFinite(Number(data?.credits?.salesCredits))) {
				setSalesCredits(Number(data.credits.salesCredits));
			}
			if (Number.isFinite(Number(data?.credits?.creditsUsed))) {
				setCreditsUsed(Number(data.credits.creditsUsed));
			}
		} catch (err) {
			setError(err?.message || 'Error starting video job.');
		} finally {
			setSubmitting(false);
		}
	};

	const handleBrandReferenceChange = async (event) => {
		const file = event.target.files?.[0] || null;

		if (!file) {
			setBrandReferenceFile(null);
			setBrandReferenceSize('');
			return;
		}

		if (!BRAND_REFERENCE_ALLOWED_MIME_TYPES.has(String(file.type || '').toLowerCase())) {
			setError('Brand reference must be JPG, PNG, or WEBP.');
			event.target.value = '';
			return;
		}

		if (file.size > MAX_BRAND_REFERENCE_BYTES) {
			setError('Brand reference image must be 10MB or smaller.');
			event.target.value = '';
			return;
		}

		let objectUrl = null;
		try {
			const dimensions = await new Promise((resolve, reject) => {
				const image = new Image();
				objectUrl = URL.createObjectURL(file);
				image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
				image.onerror = () => reject(new Error('Unable to read image dimensions.'));
				image.src = objectUrl;
			});

			const nextSize = `${dimensions.width}x${dimensions.height}`;
			if (!BRAND_REFERENCE_ALLOWED_SIZES.has(nextSize)) {
				setError('Reference image must be exactly one of: 1280x720, 720x1280, 1792x1024, 1024x1792.');
				event.target.value = '';
				return;
			}

			setBrandReferenceSize(nextSize);
		} catch (dimensionError) {
			setError(dimensionError?.message || 'Could not read reference image dimensions.');
			event.target.value = '';
			return;
		} finally {
			if (objectUrl) {
				URL.revokeObjectURL(objectUrl);
			}
		}

		setError('');
		setBrandReferenceFile(file);
	};

	const fillSceneFromPayload = (sceneLabel, item, defaultEnv = 'Office') =>
		createInitialScene(sceneLabel, {
			description: String(item?.description || '').trim(),
			camera: CAMERA_OPTIONS.includes(item?.camera) ? item.camera : 'Wide Shot',
			characters: String(item?.characters || '').trim(),
			environment: ENVIRONMENT_OPTIONS.includes(item?.environment) ? item.environment : defaultEnv,
			action: String(item?.action || '').trim(),
			mood: String(item?.mood || '').trim(),
		});

	const handleGenerateStoryboardWithAi = async () => {
		if (isPermissionRestricted) {
			setError('Your permission level does not allow AI features.');
			return;
		}

		if (!campaignBrief.trim()) {
			setError('Please enter a campaign brief before using AI storyboard generation.');
			return;
		}

		setStoryboardAiLoading(true);
		setError('');
		try {
			const response = await fetch(`${API_BASE}/leads/sora/storyboard`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					brief: campaignBrief.trim(),
					style,
					tone,
					visualStyle,
					productName,
					callToAction,
					characterType,
					characterStyle,
					characterAge,
					additionalCharacters: characterList,
				}),
			});

			const payload = await response.json();
			if (!response.ok || !payload?.success) {
				throw new Error(payload?.message || 'Failed to generate storyboard');
			}

			const scenes = Array.isArray(payload?.scenes) ? payload.scenes : [];
			if (scenes[0]) setScene1(fillSceneFromPayload('Scene 1 – Beginning', scenes[0], 'Office'));
			if (scenes[1]) setScene2(fillSceneFromPayload('Scene 2 – Middle', scenes[1], 'Tech Office'));
			if (scenes[2]) setScene3(fillSceneFromPayload('Scene 3 – End', scenes[2], 'Outdoors'));
		} catch (err) {
			setError(err.message || 'Unable to generate storyboard with AI.');
		} finally {
			setStoryboardAiLoading(false);
		}
	};

	const openSettingsModal = async () => {
		setSettingsError('');
		setSettingsFeedback('');
		setPasswordError('');
		setPasswordFeedback('');
		setSettingsModalOpen(true);
		await fetchCompanyCredits();
	};

	const handleCompanyFieldChange = (field, value) => {
		setCompanyForm((prev) => ({ ...prev, [field]: value }));
	};

	const saveCompanyProfile = async () => {
		setSettingsSaving(true);
		setSettingsError('');
		setSettingsFeedback('');
		try {
			const response = await fetch(`${API_BASE}/leads/editcompany`, {
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify(companyForm),
			});
			const payload = await response.json();
			if (!response.ok || !payload?.success) {
				throw new Error(payload?.message || 'Failed to save company profile.');
			}
			setSettingsFeedback('Company profile updated successfully.');
			await fetchCompanyCredits();
		} catch (err) {
			setSettingsError(err.message || 'Failed to save company profile.');
		} finally {
			setSettingsSaving(false);
		}
	};

	const savePasswordChange = async () => {
		setPasswordError('');
		setPasswordFeedback('');

		if (!passwordOne || !passwordTwo) {
			setPasswordError('Both password fields are required.');
			return;
		}

		if (passwordOne !== passwordTwo) {
			setPasswordError('Passwords do not match.');
			return;
		}

		setPasswordSaving(true);
		try {
			const response = await fetch(`${API_BASE}/leads/passwordchange`, {
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ password: passwordOne, confirmPassword: passwordTwo }),
			});
			const payload = await response.json();
			if (!response.ok || !payload?.success) {
				throw new Error(payload?.message || 'Failed to change password.');
			}
			setPasswordFeedback('Password changed successfully.');
			setPasswordOne('');
			setPasswordTwo('');
		} catch (err) {
			setPasswordError(err.message || 'Failed to change password.');
		} finally {
			setPasswordSaving(false);
		}
	};

	const creditsRemaining = Math.max(0, Number(salesCredits) || 0);
	const selectedDuration = Number(duration);
	const selectedDurationCost = DURATION_CREDIT_COST[selectedDuration] || 0;
	const canGenerate = finalPrompt.trim() && selectedDurationCost > 0 && creditsRemaining >= selectedDurationCost;

	const getCharacterLabel = (character) => [character.type, character.role].filter(Boolean).join(' • ');

	const toggleSceneCharacter = (scene, setter, characterLabel) => {
		const existingCharacters = String(scene.characters || '')
			.split(',')
			.map((item) => item.trim())
			.filter(Boolean);

		const nextCharacters = existingCharacters.includes(characterLabel)
			? existingCharacters.filter((item) => item !== characterLabel)
			: [...existingCharacters, characterLabel];

		setter((prev) => ({ ...prev, characters: nextCharacters.join(', ') }));
	};

	const saveDraftNow = () => {
		if (typeof window === 'undefined') return;
		const savedAt = new Date().toISOString();

		const payload = {
			campaignBrief,
			duration,
			style,
			tone,
			visualStyle,
			characterType,
			characterStyle,
			characterAge,
			lighting,
			timeOfDay,
			motionSpeed,
			brandColour,
			productName,
			brandReferenceType,
			callToAction,
			characterList,
			scene1,
			scene2,
			scene3,
			savedAt,
		};

		window.localStorage.setItem(AD_STUDIO_DRAFT_KEY, JSON.stringify(payload));
		setLastSavedAt(savedAt);
		setDraftMessage('Draft saved locally. You can return later.');
	};

	const clearSavedDraft = () => {
		if (typeof window === 'undefined') return;
		window.localStorage.removeItem(AD_STUDIO_DRAFT_KEY);
		setLastSavedAt('');
		setDraftMessage('Saved draft cleared from this browser.');
	};

	// Imagination derived values
	const imaginationSectors = useMemo(
		() => [...new Set(imaginationLeads.map(l => String(l.Sector || l.sector || '').trim()).filter(Boolean))].slice(0, 15),
		[imaginationLeads]
	);
	const imaginationLocations = useMemo(
		() => [...new Set(imaginationLeads.map(l => String(l.Location || l.location || '').trim()).filter(Boolean))].slice(0, 15),
		[imaginationLeads]
	);

	const handleGetImaginationIdea = async () => {
		setImaginationLoading(true);
		setImaginationIdea(null);
		setImaginationDisplayText('');
		if (imaginationIntervalRef.current) {
			clearInterval(imaginationIntervalRef.current);
			imaginationIntervalRef.current = null;
		}
		try {
			const response = await fetch(`${API_BASE}/leads/avenoridea`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ prompt: imaginationPrompt.trim() }),
			});
			const data = await response.json();
			if (!response.ok || !data.success) {
				throw new Error(data?.message || 'Failed to get idea');
			}
			const idea = data.idea;
			setImaginationIdea(idea);
			const fullText = String(idea?.summary || '').trim();
			let charIndex = 0;
			setImaginationDisplayText('');
			imaginationIntervalRef.current = setInterval(() => {
				charIndex++;
				setImaginationDisplayText(fullText.slice(0, charIndex));
				if (charIndex >= fullText.length) {
					clearInterval(imaginationIntervalRef.current);
					imaginationIntervalRef.current = null;
				}
			}, 18);
		} catch (err) {
			setImaginationDisplayText('Failed to generate idea. Please try again.');
		} finally {
			setImaginationLoading(false);
		}
	};

	const handleFillFromImagination = () => {
		if (!imaginationIdea) return;
		const idea = imaginationIdea;
		if (idea.campaignBrief) setCampaignBrief(idea.campaignBrief);
		if (idea.duration) setDuration(String(idea.duration));
		if (idea.style) setStyle(idea.style);
		if (idea.tone) setTone(idea.tone);
		if (idea.visualStyle) setVisualStyle(idea.visualStyle);
		if (idea.characterType) setCharacterType(idea.characterType);
		if (idea.characterStyle) setCharacterStyle(idea.characterStyle);
		if (idea.characterAge) setCharacterAge(idea.characterAge);
		if (idea.lighting) setLighting(idea.lighting);
		if (idea.timeOfDay) setTimeOfDay(idea.timeOfDay);
		if (idea.motionSpeed) setMotionSpeed(idea.motionSpeed);
		if (idea.brandColour) setBrandColour(idea.brandColour);
		if (idea.productName) setProductName(idea.productName);
		if (idea.callToAction) setCallToAction(idea.callToAction);
		if (Array.isArray(idea.characterList) && idea.characterList.length > 0) setCharacterList(idea.characterList);
		if (idea.scene1) setScene1(fillSceneFromPayload('Scene 1 – Beginning', idea.scene1, 'Office'));
		if (idea.scene2) setScene2(fillSceneFromPayload('Scene 2 – Middle', idea.scene2, 'Tech Office'));
		if (idea.scene3) setScene3(fillSceneFromPayload('Scene 3 – End', idea.scene3, 'Outdoors'));
	};

	const renderSceneEditor = (scene, setter) => (
		<div className="rounded-xl border border-white/60 bg-white/75 p-4 shadow-sm">
			<h3 className="mb-3 text-sm font-semibold text-slate-900">{scene.label}</h3>
			<div className="grid gap-3 sm:grid-cols-2">
				<div className="sm:col-span-2">
					<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Scene Description</label>
					<textarea
						value={scene.description}
						onChange={(e) => setter((prev) => ({ ...prev, description: e.target.value }))}
						rows={2}
						className={glassInputClass}
					/>
				</div>
				<div>
					<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Environment</label>
					<select
						value={scene.environment}
						onChange={(e) => setter((prev) => ({ ...prev, environment: e.target.value }))}
						className={glassInputClass}
					>
						{ENVIRONMENT_OPTIONS.map((option) => (
							<option key={option} value={option}>{option}</option>
						))}
					</select>
				</div>
				<div>
					<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Camera Angle</label>
					<select
						value={scene.camera}
						onChange={(e) => setter((prev) => ({ ...prev, camera: e.target.value }))}
						className={glassInputClass}
					>
						{CAMERA_OPTIONS.map((option) => (
							<option key={option} value={option}>{option}</option>
						))}
					</select>
				</div>
				<div>
					<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Characters</label>
					<input
						value={scene.characters}
						onChange={(e) => setter((prev) => ({ ...prev, characters: e.target.value }))}
						className={glassInputClass}
					/>
					{characterList.length > 0 && (
						<div className="mt-2 flex flex-wrap gap-1.5">
							{characterList.map((character, index) => {
								const characterLabel = getCharacterLabel(character);
								const isActive = String(scene.characters || '').includes(characterLabel);

								return (
									<button
										key={`${scene.label}-character-${index}`}
										type="button"
										onClick={() => toggleSceneCharacter(scene, setter, characterLabel)}
										className={`rounded-full border px-2 py-1 text-[11px] font-semibold transition ${isActive ? 'border-orange-500 bg-orange-100 text-orange-800' : 'border-slate-300 bg-white text-slate-700 hover:border-orange-300 hover:text-orange-700'}`}
									>
										{characterLabel}
									</button>
								);
							})}
						</div>
					)}
				</div>
				<div>
					<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Action</label>
					<input
						value={scene.action}
						onChange={(e) => setter((prev) => ({ ...prev, action: e.target.value }))}
						className={glassInputClass}
					/>
				</div>
				<div className="sm:col-span-2">
					<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Mood</label>
					<input
						value={scene.mood}
						onChange={(e) => setter((prev) => ({ ...prev, mood: e.target.value }))}
						className={glassInputClass}
					/>
				</div>
			</div>
		</div>
	);

	if (isPermissionRestricted) {
		return (
			<div className="relative min-h-screen bg-cover bg-center bg-no-repeat px-2 pb-6 pt-3 sm:px-4 sm:pb-8" style={pageBackgroundStyle}>
				<div className="sticky top-0 z-40 rounded-2xl border border-white/50 bg-white/65 shadow-[0_12px_35px_rgba(2,132,199,0.25)] backdrop-blur-xl">
					<div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-3 py-3 sm:px-6 md:flex-row md:items-center md:justify-between">
						<div className="flex items-center gap-3">
							<img src="/avenor.png" alt="InsightOS Avenor Logo" className="h-8 w-8" />
							<h1 className="text-base font-bold text-slate-900"> Avenor</h1>
							{showCompanyNameInMenu && (
								<span className="rounded-md border border-indigo-200/80 bg-indigo-100/80 px-2 py-0.5 text-[11px] font-semibold text-indigo-900 sm:text-xs">
									{companyNameForMenu}
								</span>
							)}
							<nav className="hidden items-center gap-2 md:flex">
								<NavLink to="/leads" end className={navLinkClasses}>Leads</NavLink>
							</nav>
						</div>
						<div className="flex w-full flex-wrap items-center justify-start gap-2 md:w-auto md:justify-end">
							<NavLink
								to="/logout"
								className="rounded-lg bg-gradient-to-r from-rose-600 to-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-rose-500/30 hover:from-rose-500 hover:to-red-500"
							>
								Logout
							</NavLink>
						</div>
					</div>
				</div>

				<div className="mx-auto mt-8 w-full max-w-4xl rounded-2xl border border-amber-200 bg-amber-50 px-6 py-8 shadow-sm">
					<h2 className="text-lg font-semibold text-amber-900">Ad Studio Access Restricted</h2>
					<p className="mt-2 text-sm text-amber-800">
						Your account permission level does not allow access to Enrich or AI features, including Ad Studio.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="relative min-h-screen bg-cover bg-center bg-no-repeat px-2 pb-6 pt-3 sm:px-4 sm:pb-8" style={pageBackgroundStyle}>
			<div className="sticky top-0 z-40 rounded-2xl border border-white/50 bg-white/65 shadow-[0_12px_35px_rgba(2,132,199,0.25)] backdrop-blur-xl">
				<div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-3 py-3 sm:px-6 md:flex-row md:items-center md:justify-between">
					<div className="flex items-center gap-3">
						<img src="/avenor.png" alt="InsightOS Avenor Logo" className="h-8 w-8" />
						<h1 className="text-base font-bold text-slate-900"> Avenor</h1>
						{showCompanyNameInMenu && (
							<span className="rounded-md border border-indigo-200/80 bg-indigo-100/80 px-2 py-0.5 text-[11px] font-semibold text-indigo-900 sm:text-xs">
								{companyNameForMenu}
							</span>
						)}
						<nav className="hidden items-center gap-2 md:flex">
						<NavLink to="/leads" end className={navLinkClasses}>Leads</NavLink>
						<NavLink to="/adverts" className={navLinkClasses}>Ad Studio</NavLink>
						<NavLink to="/gallery" className={navLinkClasses}>Gallery</NavLink>
						</nav>
					</div>
					<div className="flex w-full flex-wrap items-center justify-start gap-2 md:w-auto md:justify-end">
						<div className="rounded-lg border border-emerald-300/70 bg-emerald-100/75 px-3 py-1.5 text-xs font-semibold text-emerald-900 shadow-sm shadow-emerald-400/20">
							Credits Remaining: {creditsLoading ? '...' : creditsRemaining}
						</div>
						<div className="rounded-lg border border-sky-300/70 bg-sky-100/80 px-3 py-1.5 text-xs font-semibold text-sky-900 shadow-sm shadow-sky-400/20">
							Credits Used: {creditsLoading ? '...' : creditsUsed}
						</div>
						<button
							type="button"
							onClick={() => setBuyModalOpen(true)}
							className="rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-purple-500/30 hover:from-purple-500 hover:to-blue-500"
						>
							Buy
						</button>
						<button
							type="button"
							onClick={openSettingsModal}
							className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-emerald-600 to-sky-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-emerald-500/30 hover:from-emerald-500 hover:to-sky-500"
						>
							<Cog6ToothIcon className="h-4 w-4" />
							Settings
						</button>
						<NavLink
							to="/logout"
							className="rounded-lg bg-gradient-to-r from-rose-600 to-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-rose-500/30 hover:from-rose-500 hover:to-red-500"
						>
							Logout
						</NavLink>
					</div>
				</div>
				<div className="border-t border-white/50 px-4 py-2 sm:px-6 md:hidden">
					<nav className="flex items-center gap-2">
						<NavLink to="/leads" end className={navLinkClasses}>Leads</NavLink>
						<NavLink to="/adverts" className={navLinkClasses}>Ad Studio</NavLink>
						<NavLink to="/gallery" className={navLinkClasses}>Gallery</NavLink>
					</nav>
				</div>
			</div>

			<div className="min-h-screen px-1 py-4 sm:px-4 sm:py-8">
				<div className="mx-auto w-full max-w-4xl">
					<motion.section
						initial={{ opacity: 0, y: 24 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
						className="relative mb-6 overflow-hidden rounded-3xl border border-orange-300/20 p-5 shadow-[0_20px_80px_rgba(120,20,0,0.75),0_0_55px_rgba(0,0,0,0.65)] sm:p-7"
						style={{ backgroundImage: 'url(/backgroundcin.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}
					>
						<div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-black/85 via-red-950/65 to-orange-900/55" />
						<motion.div
							aria-hidden="true"
							className="pointer-events-none absolute -left-14 -top-14 h-56 w-56 rounded-full bg-red-500/45 blur-3xl"
							animate={{ x: [0, 18, 0], y: [0, 12, 0] }}
							transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
						/>
						<motion.div
							aria-hidden="true"
							className="pointer-events-none absolute -bottom-10 right-0 h-56 w-56 rounded-full bg-yellow-400/35 blur-3xl"
							animate={{ x: [0, -14, 0], y: [0, -10, 0] }}
							transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
						/>

						<div className="relative z-10 grid gap-5 sm:grid-cols-[1.1fr_0.9fr] sm:items-center">
							<div>
								<p className="inline-flex items-center rounded-full border border-orange-100/35 bg-orange-200/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-orange-100">
									Ad Studio • Sora-2 Pro
								</p>
								<h1 className="mt-3 text-2xl font-black uppercase tracking-[0.05em] text-white sm:text-3xl">
									Build your campaign story
								</h1>
								<p className="mt-3 max-w-xl text-sm text-slate-100/95 sm:text-base">
									Create your Start, Middle, and End scenes, attach characters to each phase, preview the full story flow, and generate in one place.
								</p>
							</div>
							<div className="flex justify-center sm:justify-end">
								<img src="/hollywood.png" alt="Hollywood creative headline" className="w-full max-w-xs sm:max-w-sm" />
							</div>
						</div>
					</motion.section>

					{/* Imagination Section */}
					<motion.section
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.15 }}
						className="mb-6 overflow-hidden rounded-2xl border border-violet-300/40 bg-white/70 shadow-[0_14px_50px_rgba(124,58,237,0.18)] backdrop-blur-xl"
					>
						<div className="bg-gradient-to-r from-violet-600 via-indigo-600 to-purple-700 px-5 py-4">
							<p className="text-xs font-semibold uppercase tracking-[0.15em] text-violet-100">AI Creative Engine</p>
							<h2 className="mt-1 text-xl font-black text-white">Imagination</h2>
							<p className="mt-1 text-sm text-violet-100/90">Based on your leads, we'll imagine the perfect advert idea tailored to your audience.</p>
						</div>

						<div className="p-5">
							{/* Sectors pills */}
							<div className="mb-4">
								<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
									Your Lead Sectors ({imaginationLeads.length} leads)
								</p>
								{imaginationLeadsLoading ? (
									<div className="flex flex-wrap gap-1.5">
										{Array.from({ length: 5 }).map((_, i) => (
											<div key={i} className="h-6 w-20 animate-pulse rounded-full bg-violet-100" />
										))}
									</div>
								) : (
									<div className="flex flex-wrap gap-1.5">
										{imaginationSectors.map((sector) => (
											<button
												key={sector}
												type="button"
												onClick={() => setImaginationPrompt((prev) => prev ? `${prev.trimEnd()} ${sector}` : sector)}
												className="rounded-full border border-violet-200/80 bg-violet-50/80 px-2.5 py-0.5 text-xs font-semibold text-violet-800 hover:bg-violet-200 hover:border-violet-400 transition cursor-pointer"
											>
												{sector}
											</button>
										))}
										{imaginationSectors.length === 0 && (
											<span className="text-xs text-slate-500">No sectors found — add some leads first.</span>
										)}
									</div>
								)}
							</div>

							{/* Locations pills */}
							<div className="mb-4">
								<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Locations</p>
								<div className="flex flex-wrap gap-1.5">
									{imaginationLocations.map((location) => (
										<button
											key={location}
											type="button"
											onClick={() => setImaginationPrompt((prev) => prev ? `${prev.trimEnd()} ${location}` : location)}
											className="rounded-full border border-sky-200/80 bg-sky-50/80 px-2.5 py-0.5 text-xs font-semibold text-sky-800 hover:bg-sky-200 hover:border-sky-400 transition cursor-pointer"
										>
											{location}
										</button>
									))}
									{imaginationLocations.length === 0 && !imaginationLeadsLoading && (
										<span className="text-xs text-slate-500">No locations found.</span>
									)}
								</div>
							</div>

							{/* Company Description */}
							{companyForm.Description && (
								<div className="mb-4">
									<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Your Company Description</p>
									<button
										type="button"
										onClick={() => setImaginationPrompt((prev) => prev ? `${prev.trimEnd()} ${companyForm.Description}` : companyForm.Description)}
										className="rounded-lg border border-emerald-200/80 bg-emerald-50/80 px-3 py-2 text-left text-xs text-emerald-900 hover:bg-emerald-100 hover:border-emerald-400 transition cursor-pointer w-full"
									>
										{companyForm.Description}
									</button>
								</div>
							)}

							{/* Prompt */}
							<div className="mb-4">
								<label className="block text-sm font-semibold text-slate-800">What kind of advert do you want to create?</label>
								<textarea
									value={imaginationPrompt}
									onChange={(e) => setImaginationPrompt(e.target.value)}
									rows={3}
									className={glassInputClass}
									placeholder='e.g. "I want a dramatic, cinematic feel targeting construction companies showing how we solve compliance headaches"'
								/>
							</div>

							<button
								type="button"
								onClick={handleGetImaginationIdea}
								disabled={imaginationLoading}
								className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-violet-500/30 hover:from-violet-500 hover:to-indigo-500 disabled:opacity-60"
							>
								{imaginationLoading ? (
									<>
										<span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
										Imagining…
									</>
								) : '✨ Get Video Idea'}
							</button>

							{/* Typewriter result */}
							{imaginationDisplayText && (
								<div className="mt-4 rounded-xl border border-violet-200/60 bg-gradient-to-br from-violet-50/80 to-indigo-50/60 p-4">
									<p className="text-sm font-medium leading-relaxed text-violet-900">
										{imaginationDisplayText}
										{imaginationLoading && (
											<span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-violet-600 align-text-bottom" />
										)}
									</p>

									{imaginationIdea && !imaginationLoading && (
										<div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
											<div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-violet-700">
												<span className="font-semibold">{imaginationIdea.style}</span>
												<span>·</span>
												<span>{imaginationIdea.tone}</span>
												<span>·</span>
												<span>{imaginationIdea.duration}s</span>
												<span>·</span>
												<span>{imaginationIdea.visualStyle}</span>
												<span>·</span>
												<span>{imaginationIdea.lighting} lighting</span>
											</div>
											<button
												type="button"
												onClick={handleFillFromImagination}
												className="rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2 text-sm font-black text-white shadow-lg shadow-emerald-500/30 hover:from-emerald-500 hover:to-teal-500"
											>
												Fill ↓
											</button>
										</div>
									)}
								</div>
							)}
						</div>
					</motion.section>

					<div className="rounded-2xl border border-white/50 bg-white/60 p-4 shadow-[0_20px_70px_rgba(59,130,246,0.18)] backdrop-blur-xl sm:p-6">
						<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
							<div>
								<h1 className="text-xl font-semibold text-gray-900">Avenor Ad Studio</h1>
								<p className="mt-1 text-sm text-gray-600">Create storyboard-driven videos with branded credit control.</p>
							</div>
							{(jobId || videoUrl) && (
								<button
									type="button"
									onClick={() => {
										setError('');
										resetJob();
										stopPolling();
									}}
									className="w-full rounded-md border border-white/60 bg-white/75 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white sm:w-auto"
								>
									New Video
								</button>
							)}
						</div>

						<form onSubmit={handleSubmit} className="mt-6 space-y-4">
							<div className={`${glassCardClass} border-orange-100/70`}>
								<p className="mb-3 text-sm font-semibold text-slate-900">How to use Ad Studio</p>
								<div className="space-y-2">
									<details className="rounded-xl border border-orange-200/70 bg-orange-50/70 p-3" open>
										<summary className="cursor-pointer text-sm font-semibold text-orange-900">Step 1: Write your campaign brief</summary>
										<p className="mt-2 text-xs text-orange-800">Describe your product, target audience, and desired outcome. Keep it clear and specific.</p>
									</details>
									<details className="rounded-xl border border-amber-200/70 bg-amber-50/70 p-3">
										<summary className="cursor-pointer text-sm font-semibold text-amber-900">Step 2: Set style and cast</summary>
										<p className="mt-2 text-xs text-amber-800">Choose style, tone, and character profiles. Add extra cast members as needed.</p>
									</details>
									<details className="rounded-xl border border-red-200/70 bg-red-50/70 p-3">
										<summary className="cursor-pointer text-sm font-semibold text-red-900">Step 3: Build Start → Middle → End</summary>
										<p className="mt-2 text-xs text-red-800">Fill each scene and assign characters to each phase using the scene character buttons.</p>
									</details>
									<details className="rounded-xl border border-violet-200/70 bg-violet-50/70 p-3">
										<summary className="cursor-pointer text-sm font-semibold text-violet-900">Step 4: Preview and generate</summary>
										<p className="mt-2 text-xs text-violet-800">Review the Story Flow diagram and final prompt, choose duration, then generate your video.</p>
									</details>
								</div>
							</div>

							<div className={glassCardClass}>
								<label className="block text-sm font-semibold text-slate-800">Campaign Brief</label>
								<textarea
									value={campaignBrief}
									onChange={(e) => setCampaignBrief(e.target.value)}
									rows={4}
									className={glassInputClass}
									placeholder='e.g. "Advertise my workforce management software to construction companies"'
								/>
							</div>

							<div className={`${glassCardClass} grid gap-3 sm:grid-cols-3`}>
								<p className="sm:col-span-3 text-sm font-semibold text-slate-800">Style & Tone</p>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Style</label>
									<select value={style} onChange={(e) => setStyle(e.target.value)} className={glassInputClass}>
										{STYLE_OPTIONS.map((option) => (
											<option key={option} value={option}>{option}</option>
										))}
									</select>
								</div>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Tone</label>
									<select value={tone} onChange={(e) => setTone(e.target.value)} className={glassInputClass}>
										{TONE_OPTIONS.map((option) => (
											<option key={option} value={option}>{option}</option>
										))}
									</select>
								</div>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Visual Style</label>
									<select value={visualStyle} onChange={(e) => setVisualStyle(e.target.value)} className={glassInputClass}>
										{VISUAL_STYLE_OPTIONS.map((option) => (
											<option key={option} value={option}>{option}</option>
										))}
									</select>
								</div>
							</div>

							<div className={`${glassCardClass} grid gap-3 sm:grid-cols-3`}>
								<p className="sm:col-span-3 text-sm font-semibold text-slate-800">Primary Character Preset</p>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Character Type</label>
									<select value={characterType} onChange={(e) => setCharacterType(e.target.value)} className={glassInputClass}>
										{CHARACTER_TYPE_OPTIONS.map((option) => (
											<option key={option} value={option}>{option}</option>
										))}
									</select>
								</div>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Character Style</label>
									<select value={characterStyle} onChange={(e) => setCharacterStyle(e.target.value)} className={glassInputClass}>
										{CHARACTER_STYLE_OPTIONS.map((option) => (
											<option key={option} value={option}>{option}</option>
										))}
									</select>
								</div>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Character Age</label>
									<select value={characterAge} onChange={(e) => setCharacterAge(e.target.value)} className={glassInputClass}>
										{CHARACTER_AGE_OPTIONS.map((option) => (
											<option key={option} value={option}>{option}</option>
										))}
									</select>
								</div>
							</div>

							<div className={glassCardClass}>
								<div className="mb-3 flex items-center justify-between gap-2">
									<p className="text-sm font-semibold text-slate-900">Character Cast (Multiple Characters)</p>
									<button
										type="button"
										onClick={addCharacter}
										className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500"
									>
										Add Character
									</button>
								</div>
								<div className="space-y-2">
									{characterList.map((character, index) => (
										<div key={`character-${index}`} className="grid gap-2 rounded-lg border border-white/60 bg-white/80 p-3 sm:grid-cols-4">
											<div>
												<label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600">Type</label>
												<select value={character.type} onChange={(e) => updateCharacter(index, 'type', e.target.value)} className={glassInputClass}>
													{CHARACTER_TYPE_OPTIONS.map((option) => (
														<option key={option} value={option}>{option}</option>
													))}
												</select>
											</div>
											<div>
												<label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600">Style</label>
												<select value={character.style} onChange={(e) => updateCharacter(index, 'style', e.target.value)} className={glassInputClass}>
													{CHARACTER_STYLE_OPTIONS.map((option) => (
														<option key={option} value={option}>{option}</option>
													))}
												</select>
											</div>
											<div>
												<label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600">Age</label>
												<select value={character.age} onChange={(e) => updateCharacter(index, 'age', e.target.value)} className={glassInputClass}>
													{CHARACTER_AGE_OPTIONS.map((option) => (
														<option key={option} value={option}>{option}</option>
													))}
												</select>
											</div>
											<div>
												<label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600">Role</label>
												<input
													value={character.role}
													onChange={(e) => updateCharacter(index, 'role', e.target.value)}
													className={glassInputClass}
													placeholder="e.g. Operations manager"
												/>
											</div>
											<div className="sm:col-span-4 flex justify-end">
												<button
													type="button"
													onClick={() => removeCharacter(index)}
													disabled={characterList.length === 1}
													className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-50"
												>
													Remove
												</button>
											</div>
										</div>
									))}
								</div>
							</div>

							<div className={glassCardClass}>
								<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
									<p className="text-sm font-semibold text-slate-900">Storyboard Builder</p>
									<button
										type="button"
										onClick={handleGenerateStoryboardWithAi}
										disabled={storyboardAiLoading}
										className="rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5 text-xs font-semibold text-white hover:from-indigo-500 hover:to-purple-500 disabled:opacity-60"
									>
										{storyboardAiLoading ? 'Generating Storyboard...' : 'Generate Storyboard With AI'}
									</button>
								</div>
								<div className="space-y-3">
									{renderSceneEditor(scene1, setScene1)}
									{renderSceneEditor(scene2, setScene2)}
									{renderSceneEditor(scene3, setScene3)}
								</div>
							</div>

							<div className={`${glassCardClass} border-orange-100/70`}>
								<p className="mb-3 text-sm font-semibold text-slate-900">Story Flow Preview</p>
								<div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-stretch">
									<motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} className="rounded-xl border border-orange-200/70 bg-orange-50/80 p-3">
										<p className="text-xs font-semibold uppercase tracking-wide text-orange-700">Start</p>
										<p className="mt-1 text-sm font-semibold text-slate-900">{scene1.description || 'Add beginning description'}</p>
										<p className="mt-1 text-xs text-slate-600">{scene1.characters || 'No characters selected yet'}</p>
									</motion.div>
									<div className="hidden items-center justify-center sm:flex">
										<ArrowLongRightIcon className="h-6 w-6 text-orange-500" />
									</div>
									<motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ delay: 0.08 }} className="rounded-xl border border-amber-200/70 bg-amber-50/80 p-3">
										<p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Middle</p>
										<p className="mt-1 text-sm font-semibold text-slate-900">{scene2.description || 'Add middle description'}</p>
										<p className="mt-1 text-xs text-slate-600">{scene2.characters || 'No characters selected yet'}</p>
									</motion.div>
									<div className="hidden items-center justify-center sm:flex">
										<ArrowLongRightIcon className="h-6 w-6 text-orange-500" />
									</div>
									<motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ delay: 0.16 }} className="rounded-xl border border-red-200/70 bg-red-50/80 p-3">
										<p className="text-xs font-semibold uppercase tracking-wide text-red-700">End</p>
										<p className="mt-1 text-sm font-semibold text-slate-900">{scene3.description || 'Add ending description'}</p>
										<p className="mt-1 text-xs text-slate-600">{scene3.characters || 'No characters selected yet'}</p>
									</motion.div>
								</div>
							</div>

							<div className={`${glassCardClass} grid gap-3 sm:grid-cols-3`}>
								<p className="sm:col-span-3 text-sm font-semibold text-slate-800">Visual Controls</p>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Lighting</label>
									<select value={lighting} onChange={(e) => setLighting(e.target.value)} className={glassInputClass}>
										{LIGHTING_OPTIONS.map((option) => (
											<option key={option} value={option}>{option}</option>
										))}
									</select>
								</div>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Time of Day</label>
									<select value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} className={glassInputClass}>
										{TIME_OF_DAY_OPTIONS.map((option) => (
											<option key={option} value={option}>{option}</option>
										))}
									</select>
								</div>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Motion Speed</label>
									<select value={motionSpeed} onChange={(e) => setMotionSpeed(e.target.value)} className={glassInputClass}>
										{MOTION_SPEED_OPTIONS.map((option) => (
											<option key={option} value={option}>{option}</option>
										))}
									</select>
								</div>
							</div>

							<div className={`${glassCardClass} grid gap-3 sm:grid-cols-2`}>
								<p className="sm:col-span-2 text-sm font-semibold text-slate-800">Brand Elements</p>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Product Name</label>
									<input value={productName} onChange={(e) => setProductName(e.target.value)} className={glassInputClass} />
								</div>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Brand Colour</label>
									<input value={brandColour} onChange={(e) => setBrandColour(e.target.value)} placeholder="e.g. #4f46e5 or Indigo" className={glassInputClass} />
								</div>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Logo or Product Reference</label>
									<select
										value={brandReferenceType}
										onChange={(e) => setBrandReferenceType(e.target.value === 'product' ? 'product' : 'logo')}
										className={glassInputClass}
									>
										<option value="logo">Logo Reference</option>
										<option value="product">Product Reference</option>
									</select>
									<input
										type="file"
										accept="image/jpeg,image/png,image/webp"
										onChange={handleBrandReferenceChange}
										className={`${glassInputClass} file:mr-3 file:rounded-md file:border-0 file:bg-indigo-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-indigo-700 hover:file:bg-indigo-200`}
									/>
									<p className="mt-1 text-xs text-slate-500">
										Optional. Upload JPG/PNG/WEBP (max 10MB) at 1280x720, 720x1280, 1792x1024, or 1024x1792.
									</p>
									{brandReferenceFile ? (
										<div className="mt-2 flex items-center justify-between rounded-md border border-indigo-100 bg-indigo-50 px-2 py-1 text-xs text-indigo-800">
											<span className="truncate pr-2">{brandReferenceFile.name}</span>
											<button
												type="button"
												onClick={() => {
													setBrandReferenceFile(null);
													setBrandReferenceSize('');
												}}
												className="font-semibold text-indigo-700 hover:text-indigo-900"
											>
												Remove
											</button>
										</div>
									) : null}
								</div>
								<div>
									<label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">Call To Action</label>
									<input value={callToAction} onChange={(e) => setCallToAction(e.target.value)} className={glassInputClass} />
								</div>
							</div>

							<div className={`${glassCardClass} space-y-4`}>
								<div>
									<label className="block text-base font-bold text-slate-800">Generate Preview - Seconds</label>
									<select
										value={duration}
										onChange={(e) => setDuration(e.target.value)}
										className={glassInputClass}
									>
										<option value="">Select length</option>
										<option value="4">4 seconds</option>
										<option value="8">8 seconds</option>
										<option value="12">12 seconds</option>
									</select>
								</div>

								<div className="grid gap-3 sm:grid-cols-3">
									<div className="rounded-xl border border-indigo-200/70 bg-indigo-50/80 p-3">
										<p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">4 Seconds</p>
										<p className="mt-1 text-3xl font-black text-indigo-900">10</p>
										<p className="text-sm font-semibold text-indigo-800">credits</p>
									</div>
									<div className="rounded-xl border border-amber-200/70 bg-amber-50/80 p-3">
										<p className="text-xs font-semibold uppercase tracking-wide text-amber-700">8 Seconds</p>
										<p className="mt-1 text-3xl font-black text-amber-900">20</p>
										<p className="text-sm font-semibold text-amber-800">credits</p>
									</div>
									<div className="rounded-xl border border-red-200/70 bg-red-50/80 p-3">
										<p className="text-xs font-semibold uppercase tracking-wide text-red-700">12 Seconds</p>
										<p className="mt-1 text-3xl font-black text-red-900">30</p>
										<p className="text-sm font-semibold text-red-800">credits</p>
									</div>
								</div>
							</div>

							<div className={`${glassCardClass} border-orange-100/70`}>
								<p className="mb-3 text-sm font-semibold text-slate-900">Preview Final Prompt</p>

								<div className="grid gap-3 sm:grid-cols-2">
									<div className="rounded-xl border border-white/60 bg-white/80 p-3">
										<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Campaign Brief</p>
										<p className="mt-1 text-sm text-slate-800">{campaignBrief || 'Not set yet'}</p>
									</div>
									<div className="rounded-xl border border-white/60 bg-white/80 p-3">
										<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Style & Tone</p>
										<p className="mt-1 text-sm text-slate-800">{style} · {tone} · {visualStyle}</p>
									</div>
								</div>

								<div className="mt-3 rounded-xl border border-white/60 bg-white/80 p-3">
									<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Characters</p>
									<p className="mt-1 text-sm text-slate-800">
										{characterList.length
											? characterList.map((character) => getCharacterLabel(character)).join(' | ')
											: 'No cast added yet'}
									</p>
								</div>

								<div className="mt-3 grid gap-3 sm:grid-cols-3">
									<div className="rounded-xl border border-orange-200/70 bg-orange-50/80 p-3">
										<p className="text-xs font-semibold uppercase tracking-wide text-orange-700">Start</p>
										<p className="mt-1 text-sm font-semibold text-slate-900">{scene1.description || 'Add beginning description'}</p>
										<p className="mt-1 text-xs text-slate-600">{scene1.characters || 'No characters selected yet'}</p>
									</div>
									<div className="rounded-xl border border-amber-200/70 bg-amber-50/80 p-3">
										<p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Middle</p>
										<p className="mt-1 text-sm font-semibold text-slate-900">{scene2.description || 'Add middle description'}</p>
										<p className="mt-1 text-xs text-slate-600">{scene2.characters || 'No characters selected yet'}</p>
									</div>
									<div className="rounded-xl border border-red-200/70 bg-red-50/80 p-3">
										<p className="text-xs font-semibold uppercase tracking-wide text-red-700">End</p>
										<p className="mt-1 text-sm font-semibold text-slate-900">{scene3.description || 'Add ending description'}</p>
										<p className="mt-1 text-xs text-slate-600">{scene3.characters || 'No characters selected yet'}</p>
									</div>
								</div>

								<details className="mt-3 rounded-md border border-white/60 bg-white/75 p-3">
									<summary className="cursor-pointer text-sm font-semibold text-slate-700">View raw prompt text</summary>
									<textarea
										value={finalPrompt}
										readOnly
										rows={7}
										className="mt-2 block w-full rounded-md border border-white/70 bg-white/90 px-3 py-2 text-xs text-slate-700"
									/>
								</details>
							</div>

							<div className={`${glassCardClass} text-xs text-slate-700`}>
								Selected duration cost: <span className="font-semibold">{selectedDurationCost || 0} credits</span>
								{' '}· Available now: <span className="font-semibold">{creditsRemaining}</span>
							</div>

							<div className={`${glassCardClass} flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between`}>
								<div className="text-xs font-semibold text-slate-700">
									Draft is auto-saved in this browser.
									{lastSavedAt ? ` Last saved: ${new Date(lastSavedAt).toLocaleString()}` : ''}
								</div>
								<div className="flex flex-wrap gap-2">
									<button
										type="button"
										onClick={saveDraftNow}
										className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500"
									>
										Save Draft
									</button>
									<button
										type="button"
										onClick={clearSavedDraft}
										className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
									>
										Delete Last Saved
									</button>
								</div>
							</div>

							{draftMessage && (
								<div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-2 text-xs font-semibold text-sky-800">
									{draftMessage}
								</div>
							)}

							{error && (
								<div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
							)}

							<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
								<button
									type="submit"
									disabled={submitting || !canGenerate}
									className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 sm:w-auto"
								>
									{submitting ? 'Starting…' : 'Generate Video'}
								</button>

								{jobId && (
									<div className="text-sm text-gray-600">
										<span className="font-medium text-gray-800">Job:</span> {jobId}
									</div>
								)}
							</div>
						</form>
					</div>

					{(jobId || videoUrl) && (
						<div className="mt-6 rounded-2xl border border-white/55 bg-white/70 p-6 shadow-[0_14px_40px_rgba(15,23,42,0.2)] backdrop-blur-xl">
							<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
								<div>
									<h2 className="text-lg font-semibold text-gray-900">Output</h2>
									<p className="mt-1 text-sm text-gray-600">Status: <span className="font-medium text-gray-800">{status}</span></p>
								</div>
								{downloadUrl && (
									<a
										href={downloadUrl}
										className="inline-flex w-full items-center justify-center rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black sm:w-auto"
										target="_blank"
										rel="noreferrer"
									>
										Download
									</a>
								)}
							</div>

							<div className="mt-4">
								{gallerySaving && (
									<p className="mb-2 text-xs font-semibold text-indigo-700">Saving to gallery...</p>
								)}
								<div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
									<div className="h-full bg-indigo-600" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
								</div>
								<p className="mt-2 text-xs text-gray-500">{Math.round(progress)}%</p>
							</div>

							<div className="mt-5 overflow-hidden rounded-lg bg-black">
								{videoUrl ? (
									<video src={videoUrl} controls className="h-52 w-full object-contain sm:h-auto" />
								) : (
									<div className="flex h-52 items-center justify-center text-sm text-gray-200 sm:h-64">Waiting for video…</div>
								)}
							</div>
						</div>
					)}
				</div>
			</div>

			<CreditBuyModal
				open={buyModalOpen}
				onClose={() => setBuyModalOpen(false)}
				creditsRemaining={creditsRemaining}
				creditsUsed={creditsUsed}
			/>

			<CompanySettingsModal
				open={settingsModalOpen}
				onClose={() => setSettingsModalOpen(false)}
				companyForm={companyForm}
				onCompanyFieldChange={handleCompanyFieldChange}
				onSaveCompany={saveCompanyProfile}
				settingsSaving={settingsSaving}
				settingsFeedback={settingsFeedback}
				settingsError={settingsError}
				passwordOne={passwordOne}
				passwordTwo={passwordTwo}
				onPasswordOneChange={setPasswordOne}
				onPasswordTwoChange={setPasswordTwo}
				onSavePassword={savePasswordChange}
				passwordSaving={passwordSaving}
				passwordFeedback={passwordFeedback}
				passwordError={passwordError}
				savedEmailFooterHtml={savedEmailFooterHtml}
			/>
		</div>
	);
}

